/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, {
    useEffect, createRef, useRef, useState,
    useContext,
} from 'react';
import {
    number, object, string, array, shape, arrayOf,
    bool,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useUIDSeed } from 'react-uid';
import LazyLoad from 'react-lazyload';
import ReactHtmlParser from 'react-html-parser';
import { Paper } from '@material-ui/core';
import Slider from 'react-slick';
import ResponsiveImage from '../../../../../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import pmallHelper from '../../../../../../../helpers/pmall';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import SnipeRenderQuery from '../../../common/SnipeRenderQuery';
import { sliderFocusOnTouch } from '../../../../../../../helpers/common/helper';
import ProductImageMagnifierUI from '../../ProductImageMagnifierUI';
import { getFeatureFlag, getFeatureFlags } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { CrossMerch } from '../../../../../CrossMerchContainer/CrossMerchContainer';
import GraphqlUpdateProductImageSlider from '../../GraphqlUpdateProductImageSlider';
import FavoritesUI from '../../../../../GraphqlCommonComponents/Favourite/FavouriteUi';

const slideImageUI = ({
    classes, productName, path, name, snipe, snipeImagePath, pmallSummaryInfo, partNumber, isSnipeImageShow, renderSnipeImage, featureFlags, isPdpRedesignEnabled, // changed to destructive object to avoid conflicting with positioning args
}) => {
    const responsiveImagePath = () => {
        const extension = (name || '').includes('_gif') ? '.gif' : 'x.jpg';
        return `${path + name + extension}?height=755&width=690`;
    };
    const pdpImageZoomEnabled = featureFlags['is-pdp-product-image-zoom-enabled'] && !isPdpRedesignEnabled;
    let imagePath = responsiveImagePath();
    let isExternalSource = false;

    if (name === pmallSummaryInfo?.customizations?.imageURL) {
        imagePath = pmallHelper.buildURL(pmallSummaryInfo, true);
        isExternalSource = true;
    }
    const data = {
        name: productName,
        partNumber,
        image: {
            snipe,
            path: snipeImagePath,
            altText: name,
        },
    };
    return (
        <div className={classes.imageContainer}>
            {pdpImageZoomEnabled && typeof window !== 'undefined'
                ? (
                    <ProductImageMagnifierUI
                        imagePath={imagePath}
                        data={data}
                        classes={classes}
                        isSnipeImageShow={!isSnipeImageShow && !!snipe}
                        renderSnipeImage={renderSnipeImage}
                    />
                ) : (
                    <>
                        <ResponsiveImage
                            alt={productName}
                            path={imagePath}
                            fullWidth
                            className={classes.imageWrapper}
                            isExternalSource={isExternalSource}
                            params={{ desktop: 'auto=webp', mobile: 'auto=webp', tablet: 'auto=webp' }}
                        />
                        {snipe && <img className={classes.snipe} alt={snipe} src={`${snipeImagePath}${snipe}_z.gif`} />}
                        <SnipeRenderQuery  partNumber={partNumber}  productType="product" />
                        <FavoritesUI partNumber={partNumber || data?.partNumber || data?.id} />
                    </>
                )}
        </div>
    );
};
const ProductImageUIMobileBlock = ({
    classes,
    productName,
    product,
    name,
    path,
    snipe,
    snipeImagePath,
    additionalImages,
    productNameDisplay,
    pmallSummaryInfo,
    selectedSku,
    isPdpRedesignEnabled,
}) => {
    const containerRef = createRef();
    useEffect(() => {
        sliderFocusOnTouch(containerRef);
    });

    const { featureFlags: flags = {} } = useContext(CrossMerch);
    const seed = useUIDSeed();
    const slider = useRef();
    const [previousIndex, setPreviousIndex] = useState(null);
    const featureFlags = useSelector(getFeatureFlags);
    const [isSnipeImageShow, setSnipeImageShow] = useState(false);
    const [enableZoom, setEnableZoom] = useState(false);

    const dispatch = useDispatch();
    const altImageArray = [];
    // TODO Chnage to productNameDisplay === "Overlay", Once the value is updated on 18F
    const isProductNameOnOverlay = productNameDisplay !== 'Normal' && productNameDisplay !== null;
    // const minHeight = presentation_family === 'flower' ? { minHeight: 454 } : { minHeight: 450 };
    let primaryImageName = name;
    let additionalImageData = [...additionalImages] ?? [];

    const hasPmallData = pmallSummaryInfo?.customizations?.refnum && pmallSummaryInfo?.customizations?.refnum !== '';

    const isPmallPersonalizationAttr = (selectedSku?.attributes || [])?.find((attr) => attr.name === 'is_pmall_personalization_enabled');
    const isPmallPersonalizationEnabled = isPmallPersonalizationAttr?.values?.[0]?.value === 'true';
    const isPmallPersonalizationTypeAttr = (selectedSku?.attributes || [])?.find((attr) => attr.name === 'personalization_type_enabled');
    const pmallPersonalizationType = !!isPmallPersonalizationTypeAttr?.values?.[0]?.value;
    const renderSnipeImage = (value) => {
        if (enableZoom && value) {
            setSnipeImageShow(true);
        } else {
            setSnipeImageShow(false);
        }
    };

    /**
     * if pmall data is present it means there is a preview we want
     * to show first in the slide show. Set what was to be the
     * primary (first) image as the first additional image
     * and set the preview to be primary.
     */
    if (hasPmallData) {
        // capture whats was supposed to be the pirmary image and place it sencond
        additionalImageData = [primaryImageName, ...additionalImageData];
        // make the primary image the preview
        primaryImageName = pmallSummaryInfo.customizations.imageURL;
    }
    const partNumber = product.partNumber || product.id;

    altImageArray.push(slideImageUI({
        classes, productName, path, name: primaryImageName, snipe, snipeImagePath, pmallSummaryInfo, partNumber, isSnipeImageShow, renderSnipeImage, featureFlags, isPdpRedesignEnabled,
    }));

    if (product?.brandId === '1030' || product?.brandId === '1033' || isPmallPersonalizationEnabled || pmallPersonalizationType) {
        if (additionalImageData !== null && additionalImageData?.length > 0) {
            additionalImageData?.map((item) => {
                altImageArray.push(
                    <LazyLoad key={seed(item)}>
                        { slideImageUI({
                            classes, productName, path, name: item, snipe, snipeImagePath, pmallSummaryInfo, partNumber, isSnipeImageShow, renderSnipeImage, featureFlags, isPdpRedesignEnabled,
                        })}
                    </LazyLoad>,
                );
            });
        } else {
            selectedSku?.image?.productThumbnails?.map((item) => {
                if (item?.name !== primaryImageName) {
                    altImageArray.push(
                        <LazyLoad key={seed(item)}>
                            { slideImageUI({
                                classes, productName, path: item?.path, name: item?.name, snipe, snipeImagePath, pmallSummaryInfo, partNumber, isSnipeImageShow, renderSnipeImage, featureFlags, isPdpRedesignEnabled,
                            })}
                        </LazyLoad>,
                    );
                }
            });
        }
    }

    if (additionalImageData.length > 0 && product.brandId !== '1030' && product.brandId !== '1033' && !isPmallPersonalizationEnabled &&  !pmallPersonalizationType) {
        additionalImageData.forEach((img) => {
            altImageArray.push(
                <LazyLoad key={seed(img)}>
                    {slideImageUI({
                        classes, productName, path, name: img, snipe, snipeImagePath, pmallSummaryInfo, partNumber, isSnipeImageShow, renderSnipeImage, featureFlags, isPdpRedesignEnabled,
                    }) }
                </LazyLoad>,
            );
        });
    }

    // Ga tagging event function
    const trackEventClick = (item, count) => {
        dispatch(trackEvent({
            eventCategory: 'product image',
            eventAction: count > previousIndex ?   'swiped right' : 'swiped left',
            eventLabel: `${product?.baseCode} | ${item} | ${count + 1}`,

            ga_event_name: 'image_carousel_interaction',
            image_url: item,
            image_alt: product?.baseCode,
            image_index: count + 1,
            image_count: altImageArray.length,
        }));
    };
    const track = (value) => {
        let imageUrl = '';
        setPreviousIndex(value);
        const slideElement = slider.current.innerSlider.list.querySelector(`[data-index="${value}"]`);
        if (altImageArray.length > 0) {
            if (slideElement?.getElementsByTagName('video')?.[0]) { // for video
                imageUrl =  slideElement.getElementsByTagName('video')[0].poster;
                trackEventClick(imageUrl, value);
            } else if (slideElement?.getElementsByTagName('img')?.[0]) {
                imageUrl = new URL(slideElement?.getElementsByTagName('img')[0]?.src);
                imageUrl = imageUrl.origin + imageUrl.pathname;
                trackEventClick(imageUrl, value);
            }
        }
    };
    const productNameContainer = () => (
        altImageArray?.length > 1 ? (
            <div className={isProductNameOnOverlay ? `${classes.productNameOnOverlay}` : ''}>
                {isProductNameOnOverlay ?  (
                    <div className={` ${classes.productNameContainer}`}>
                        <div className={classes.productName}>{ReactHtmlParser(productName)}</div>
                    </div>
                ) : null}
            </div>

        ) : (
            isProductNameOnOverlay  && (
                <div className={`${classes.productNameContainer} ${classes.singleProduct}`}>
                    <div className={classes.productName}>{ReactHtmlParser(productName)}</div>
                </div>
            )
        )
    );
    return (
        <>
            {flags['is-updated-mobile-pdp-slider']
                ? (
                    <GraphqlUpdateProductImageSlider
                        classes={classes}
                        containerRef={containerRef}
                        altImageArray={altImageArray}
                        track={track}
                        isProductNameOnOverlay={isProductNameOnOverlay}
                        slider={slider}
                        productNameContainer={productNameContainer}
                        setEnableZoom={setEnableZoom}
                        renderSnipeImage={renderSnipeImage}
                        productImageV2Block
                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                    />
                )
                : (
                    <Paper
                        ref={containerRef}
                        className={`${classes.root} ${isPdpRedesignEnabled ? classes.rootWrapper : ''}`}
                        onPointerDown={() => setEnableZoom(true)}
                        onPointerMove={() =>  setEnableZoom(false)}
                        onPointerUp={() => renderSnipeImage(true)}
                    >
                        <Slider
                            dots
                            speed={500}
                            infinite={false}
                            slidesToShow={1}
                            slidesToScroll={1}
                            arrows={false}
                            cssEase="linear"
                            ref={slider}
                            swipe={!isSnipeImageShow}
                            className={`carousel-container ${classes.carouselContainer} ${isProductNameOnOverlay && altImageArray.length > 1  ? classes.slickOverlay : ''} ${ altImageArray.length > 1 ? classes.pdpAltScrollBar : ''}`}
                            dotsClass={`slick-dots slick-thumb ${isProductNameOnOverlay ? classes.slickOverlay : ''}`}
                            customPaging={() => <div className={classes.dotNavigation} />}
                            afterChange={(current) => { track(current); }}
                        >
                            {altImageArray?.map((item) => (
                                <div key={seed(item)}>
                                    { item }
                                </div>
                            ))}
                        </Slider>
                        {productNameContainer()}
                    </Paper>
                )}
        </>
    );
};

ProductImageUIMobileBlock.propTypes = {
    classes: object.isRequired,
    product: shape({
        baseCode: string.isRequired,
    }).isRequired,
    productName: arrayOf(string.isRequired).isRequired,
    path: string.isRequired,
    snipe: string.isRequired,
    name: string.isRequired,
    snipeImagePath: string.isRequired,
    additionalImages: array.isRequired,
    productNameDisplay: string.isRequired,
    pmallSummaryInfo: shape({
        customization: shape({
            count: number,
            refnum: string,
            mobileURL: string,
            imageURL: string,
        }),
    }),
    selectedSku: shape({
        image: shape({
            productThumbnails: shape({
                name: string.isRequired,
                path: string.isRequired,
            }),
        }),
    }),
    isPdpRedesignEnabled: bool,
};

ProductImageUIMobileBlock.defaultProps = {
    pmallSummaryInfo: {
        constomizations: {
            count: 0,
            refnum: '',
            mobileURL: '',
            imageURL: '',
        },
    },
    selectedSku: {},
    isPdpRedesignEnabled: false,
};

export default ProductImageUIMobileBlock;
